import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Code2, Sun, Moon, LogOut, User, Award, Trophy, Star, Zap, 
  Flame, Medal, Target, Crown, ChevronRight, TrendingUp, Gift
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { gamificationApi, labProgressApi } from '../lib/api';
import { toast } from 'sonner';

// Badge icon mapping
const getBadgeIcon = (icon, className = "w-6 h-6") => {
  const icons = {
    award: Award,
    star: Star,
    zap: Zap,
    trophy: Trophy,
    medal: Medal,
    flame: Flame,
    target: Target
  };
  const IconComponent = icons[icon] || Award;
  return <IconComponent className={className} />;
};

export default function Gamification() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [labGameStats, setLabGameStats] = useState(null);
  const [labLeaderboard, setLabLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'lab-games' && !labGameStats) {
      Promise.all([
        labProgressApi.getGamificationStats(),
        labProgressApi.getGamificationLeaderboard()
      ]).then(([s, lb]) => { setLabGameStats(s); setLabLeaderboard(lb); })
        .catch(() => toast.error('Failed to load lab game stats'));
    }
  }, [activeTab, labGameStats]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsData, leaderboardData] = await Promise.all([
        gamificationApi.getMyStats(),
        gamificationApi.getLeaderboard()
      ]);
      setStats(statsData);
      setLeaderboard(leaderboardData);
    } catch (error) {
      toast.error('Failed to load gamification data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading achievements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="gamification-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-xl font-bold">
              <Code2 className="w-7 h-7 text-primary" />
              <span>StudyCode</span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors">Dashboard</Link>
              <Link to="/courses" className="text-muted-foreground hover:text-foreground transition-colors">Courses</Link>
              <Link to="/gamification" className="text-primary font-medium">Achievements</Link>
            </nav>

            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={toggleTheme}>
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="w-4 h-4" />
                    </div>
                    <span className="hidden md:inline">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem>
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Points Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Trophy className="w-8 h-8 text-yellow-500" />
                Your Achievements
              </h1>
              <p className="text-muted-foreground mt-1">Track your progress and earn rewards</p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="px-6 py-3 rounded-xl bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/30">
                <div className="text-3xl font-bold text-yellow-500">{stats?.points?.total_points || 0}</div>
                <div className="text-sm text-muted-foreground">Total Points</div>
              </div>
              <div className="px-6 py-3 rounded-xl bg-gradient-to-r from-primary/20 to-primary/10 border border-primary/30">
                <div className="text-3xl font-bold text-primary">#{stats?.rank || '-'}</div>
                <div className="text-sm text-muted-foreground">Your Rank</div>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-card border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats?.points?.chapters_completed || 0}</div>
                  <div className="text-xs text-muted-foreground">Chapters Done</div>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-card border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Award className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats?.points?.exams_passed || 0}</div>
                  <div className="text-xs text-muted-foreground">Exams Passed</div>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-card border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats?.points?.high_scores || 0}</div>
                  <div className="text-xs text-muted-foreground">High Scores (90%+)</div>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg bg-card border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats?.points?.perfect_scores || 0}</div>
                  <div className="text-xs text-muted-foreground">Perfect Scores</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 border-b-2 transition-colors ${activeTab === 'overview' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Badges
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`px-4 py-2 border-b-2 transition-colors ${activeTab === 'leaderboard' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Leaderboard
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-2 border-b-2 transition-colors ${activeTab === 'activity' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            Activity
          </button>
          <button
            onClick={() => setActiveTab('lab-games')}
            className={`px-4 py-2 border-b-2 transition-colors ${activeTab === 'lab-games' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid="lab-games-tab"
          >
            Lab Games
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Earned Badges */}
            <div>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Earned Badges ({stats?.badges?.length || 0})
              </h2>
              {stats?.badges?.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {stats.badges.map((badge) => (
                    <div key={badge.badge_id} className="p-4 rounded-lg bg-gradient-to-br from-yellow-500/10 to-amber-500/5 border border-yellow-500/30">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-500">
                          {getBadgeIcon(badge.icon, "w-7 h-7")}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold">{badge.name}</h3>
                          <p className="text-sm text-muted-foreground">{badge.description}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Earned: {new Date(badge.earned_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Award className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Complete courses and exams to earn badges!</p>
                </div>
              )}
            </div>

            {/* Available Badges */}
            <div>
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5" />
                Available Badges
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats?.available_badges?.filter(b => !b.earned).map((badge) => (
                  <div key={badge.badge_id} className="p-4 rounded-lg bg-card border border-border opacity-70 hover:opacity-100 transition-opacity">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                        {getBadgeIcon(badge.icon, "w-7 h-7")}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{badge.name}</h3>
                        <p className="text-sm text-muted-foreground">{badge.description}</p>
                        <Badge variant="outline" className="mt-2">{badge.points} pts</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-500" />
                {leaderboard?.scope === 'school' ? 'School Leaderboard' : 'Global Leaderboard'}
              </h2>
              <Badge variant="outline">Top {leaderboard?.leaderboard?.length || 0} students</Badge>
            </div>

            <div className="rounded-lg border border-border overflow-hidden">
              {leaderboard?.leaderboard?.map((entry, idx) => (
                <div 
                  key={entry.user_id} 
                  className={`flex items-center gap-4 p-4 ${entry.is_me ? 'bg-primary/5' : ''} ${idx > 0 ? 'border-t border-border' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    idx === 0 ? 'bg-yellow-500 text-white' :
                    idx === 1 ? 'bg-gray-400 text-white' :
                    idx === 2 ? 'bg-amber-600 text-white' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {idx < 3 ? <Crown className="w-5 h-5" /> : entry.rank}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2">
                      {entry.name}
                      {entry.is_me && <Badge variant="secondary" className="text-xs">You</Badge>}
                    </div>
                    <div className="text-sm text-muted-foreground">{entry.badges_count} badges</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">{entry.total_points}</div>
                    <div className="text-xs text-muted-foreground">points</div>
                  </div>
                </div>
              ))}
            </div>

            {leaderboard?.my_rank > (leaderboard?.leaderboard?.length || 0) && (
              <div className="mt-4 p-4 rounded-lg bg-card border border-border">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                    #{leaderboard.my_rank}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Your Position</div>
                    <div className="text-sm text-muted-foreground">Keep learning to climb the ranks!</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-lg">{leaderboard.my_points}</div>
                    <div className="text-xs text-muted-foreground">points</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Recent Activity
            </h2>

            {stats?.recent_activity?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_activity.map((activity, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-4 rounded-lg bg-card border border-border">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      activity.points > 0 ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
                    }`}>
                      {activity.points > 0 ? '+' : ''}{activity.points}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{activity.reason}</div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(activity.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Complete chapters and exams to see your activity!</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'lab-games' && (
          <div className="space-y-6" data-testid="lab-games-content">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-500" />
              Lab Game Records
            </h2>

            {/* Personal Records Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Best Combo', value: labGameStats?.best_combo || 0, suffix: 'x', icon: Flame, color: 'text-orange-400', bg: 'bg-orange-500/10' },
                { label: 'Speed Record', value: labGameStats?.fastest_timer != null ? `${labGameStats.fastest_timer}s` : '-', icon: Zap, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
                { label: 'Memory Best', value: labGameStats?.best_memory_turns != null ? `${labGameStats.best_memory_turns}` : '-', suffix: ' turns', icon: Target, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                { label: 'RF Streak', value: labGameStats?.best_rapid_streak || 0, icon: Zap, color: 'text-green-400', bg: 'bg-green-500/10' },
                { label: 'Catch Score', value: labGameStats?.best_catch_score || 0, icon: Star, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
                { label: 'Target Hits', value: labGameStats?.best_target_hits || 0, icon: Target, color: 'text-red-400', bg: 'bg-red-500/10' },
              ].map((stat, i) => (
                <div key={i} className="p-4 rounded-xl bg-card border border-border/50 text-center" data-testid={`game-stat-${i}`}>
                  <div className={`w-10 h-10 rounded-full ${stat.bg} flex items-center justify-center mx-auto mb-2`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}{stat.suffix && typeof stat.value === 'number' ? stat.suffix : ''}</div>
                  <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {labGameStats?.total_gamified_completed || 0} gamified missions completed
              </p>
            </div>

            {/* Lab Game Leaderboard */}
            {labLeaderboard && labLeaderboard.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-500" />
                  Lab Games Leaderboard
                </h3>
                <div className="rounded-lg border border-border overflow-hidden">
                  {labLeaderboard.map((entry, idx) => (
                    <div key={entry.user_id} className={`flex items-center gap-4 p-4 ${entry.is_me ? 'bg-primary/5' : ''} ${idx > 0 ? 'border-t border-border' : ''}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        idx === 0 ? 'bg-yellow-500 text-white' : idx === 1 ? 'bg-gray-400 text-white' : idx === 2 ? 'bg-amber-600 text-white' : 'bg-muted text-muted-foreground'
                      }`}>
                        {idx < 3 ? <Crown className="w-5 h-5" /> : idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium flex items-center gap-2">
                          {entry.name}
                          {entry.is_me && <Badge variant="secondary" className="text-xs">You</Badge>}
                        </div>
                        <div className="text-sm text-muted-foreground">{entry.gamified_missions} missions | Best combo: {entry.best_combo}x</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg">{entry.total_xp}</div>
                        <div className="text-xs text-muted-foreground">XP</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Game Activity */}
            {labGameStats?.game_records?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3">Recent Game Activity</h3>
                <div className="space-y-2">
                  {labGameStats.game_records.slice(0, 10).map((record, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 rounded-lg bg-card border border-border/50 text-sm">
                      <Badge variant="outline" className="text-xs shrink-0">{record.type}</Badge>
                      <span className="text-muted-foreground">{record.lab_id}</span>
                      <span className="ml-auto font-medium">
                        {record.type === 'streak-combo' && `${record.max_combo}x combo`}
                        {record.type === 'timer-challenge' && `${record.time_left}s left`}
                        {record.type === 'memory-card' && `${record.turns} turns`}
                        {record.type === 'rapid-fire' && `${record.best_streak} streak`}
                        {record.type === 'catch-game' && `${record.correct} caught`}
                        {record.type === 'target-shooter' && `${record.hits} hits`}
                        {record.type === 'puzzle' && `${record.score}/${record.total}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!labGameStats?.game_records?.length && (
              <div className="text-center py-8 text-muted-foreground">
                <Flame className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Complete gamified lab missions to see your records here!</p>
                <Link to="/labs">
                  <Button variant="outline" className="mt-4">Go to Visual Labs</Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
